import { Button, DesktopModal, Divider, Empty, Flex, Group, IconFont, Text } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import type { NodePutData } from '@lighthouse/shared'
import { FieldTypeTag, findNodeOption, type FlowNode, getFlowNodeIcon, LinkDocument, nodeTypeOptions } from '@lighthouse/shared'
import { useReactFlow, useStore } from '@xyflow/react'
import { memo, useMemo } from 'react'
import styled from 'styled-components'

import { useFlowContainerContext } from '@/contexts/FlowContext'

import { NODE_STATUS_COLOR, NODE_STATUS_ICON_MAP, NODE_STATUS_LABEL, NODE_STATUS_TINY_COLOR } from '../../constants'
import { ConvertNodePutData2String } from '../../utils/convertNodePutData2String'

const SCResultBox = styled.div<{ color: string; tinyColor: string }>`
    display: flex;
    margin: 16px;
    padding: 14px 8px;
    border-radius: 8px;
    border-width: 2px;
    border-style: solid;
    border-color: ${({ color }) => color || 'var(--color-gray-50)'};
    background: ${({ tinyColor }) => tinyColor};
    box-shadow: 0px 1px 2px 0px #1018280f;
    & > * {
        flex: 1;
    }
`

const SCxCardList = styled.div`
    padding-bottom: 62px;
`

const SCxCard = styled.div`
    margin: 0 0 12px 0;
    border-radius: 8px;
    border: 1px solid var(--color-gray-200);
`

const SCxItem = styled.div`
    padding: 8px;
    display: flex;
    justify-content: space-between;
    overflow: hidden;
`

const SCxCardItemLabel = styled.div`
    display: flex;
    gap: 4px;
    flex-shrink: 0;
    padding: 4px 8px;
    margin-right: 8px;
    height: 28px;
    font-size: var(--font-size-normal);
    color: var(--color-black);
    background-color: var(--color-gray-100);
    border-radius: 4px;
`

const SCxCardItemValue = styled.div`
    font-size: var(--font-size-normal);
    color: var(--color-gray-500);
    word-wrap: break-word;
    word-break: break-all;
    white-space: pre-wrap;
    line-height: 28px;
`

const SCxFieldTypeTag = styled(FieldTypeTag)`
    color: var(--color-gray-400) !important;
`

interface NodeResultProps {
    node: FlowNode
}

const NodeResult = memo(({ node }: NodeResultProps) => {
    const {
        data: { status }
    } = node
    const output = status?.output
    const icon = NODE_STATUS_ICON_MAP[status?.status || 'NOT_START']
    return (
        <>
            <SCResultBox
                color={NODE_STATUS_COLOR[status?.status || 'NOT_START']}
                tinyColor={NODE_STATUS_TINY_COLOR[status?.status || 'NOT_START']}
            >
                <Flex gap={4} direction="column" alignItems="center">
                    <Text size={12} color="var(--color-gray-500)" lineHeight="20px">
                        状态
                    </Text>
                    <Flex gap={4}>
                        {icon && <IconFont type={icon} size={20} fill={NODE_STATUS_COLOR[status?.status || 'NOT_START']} />}
                        <Text size={14} lineHeight="22px" color={NODE_STATUS_COLOR[status?.status || 'NOT_START']}>
                            {NODE_STATUS_LABEL[status?.status || 'NOT_START']}
                        </Text>
                    </Flex>
                </Flex>
                {/* <Flex gap={4} direction="column" alignItems="center">
                    <Text size={12} color="var(--color-gray-500)" lineHeight="20px">
                        运行时间
                    </Text>
                    <Text size={14} lineHeight="22px">
                        0.05s
                    </Text>
                </Flex>
                <Flex gap={4} direction="column" alignItems="center">
                    <Text size={12} color="var(--color-gray-500)" lineHeight="20px">
                        运行步数
                    </Text>
                    <Text size={14} lineHeight="22px">
                        4
                    </Text>
                </Flex> */}
            </SCResultBox>
            <Divider />
            {/* <Group label="输入">ss</Group>
            <Divider /> */}
            <Group label="输出">
                <SCxCardList>
                    {output && output.length ? (
                        output.map((o, i) => {
                            return (
                                <SCxCard key={i}>
                                    {o.map((data: NodePutData, index: number) => {
                                        return (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <SCxItem key={`${i}-${index}`}>
                                                <SCxCardItemLabel>
                                                    {data.label} <SCxFieldTypeTag innerType={data.type} />
                                                </SCxCardItemLabel>
                                                <SCxCardItemValue>{ConvertNodePutData2String(data)}</SCxCardItemValue>
                                            </SCxItem>
                                        )
                                    })}
                                </SCxCard>
                            )
                        })
                    ) : (
                        <Empty icon={<img alt="no_output" src={getAssetUrl('empty', 'no_output.svg')} />} description="无输出" />
                    )}
                </SCxCardList>
            </Group>
        </>
    )
})

export const NodeResultModal: React.FC = () => {
    const rootEl = useStore(s => s.domNode)
    const { modalState, setModalState } = useFlowContainerContext()
    const nodeId = modalState?.selectedNodeId
    const { getNodes, updateNodeData, setEdges } = useReactFlow()
    const node = useMemo(() => {
        const nodes = getNodes() as FlowNode[]
        return nodes?.find(node => node.id === nodeId)
    }, [getNodes, nodeId])

    const nodeInfo = useMemo(() => {
        if (!node) {
            return null
        }
        const foundOption = findNodeOption(node.data, nodeTypeOptions)
        const icon = getFlowNodeIcon(node.data)
        return { ...foundOption, icon }
    }, [node])

    if (!node) {
        return null
    }

    return (
        <DesktopModal
            open
            onClose={() => setModalState({})}
            title={
                <Flex gap={8} direction="column">
                    <Flex gap={8}>
                        <Flex p={4} style={{ borderRadius: 4, background: nodeInfo?.background || '#2E90FA' }}>
                            <IconFont type={nodeInfo?.icon || 'HomeLiner'} size={16} color={nodeInfo?.color || 'var(--color-white)'} />
                        </Flex>
                        <Text lineHeight="24px" weight={600}>
                            {nodeInfo?.label}
                        </Text>
                    </Flex>
                    <Text size={12} lineHeight="20px" color="var(--color-gray-500)">
                        暂无描述
                    </Text>
                </Flex>
            }
            withOverlay={false}
            closeOnClickOverlay={false}
            trapFocus={false}
            width={360}
            target={rootEl}
            extra={
                <Button type="text" icon={<LinkDocument type={node.data.nodeType} />} style={{ width: 24, height: 24, borderRadius: 3 }} />
            }
            styles={{
                root: { pointerEvents: 'none' },
                modal: {
                    pointerEvents: 'initial',
                    position: 'absolute',
                    top: 60,
                    right: 12,
                    bottom: 12
                },
                header: {
                    height: 'auto',
                    paddingTop: 16
                },
                title: {
                    lineHeight: 'unset'
                },
                actions: {
                    alignSelf: 'flex-start'
                },
                body: {
                    padding: '0 !important'
                }
            }}
            transition={{ from: { x: '100%' }, to: { x: '0%' } }}
        >
            <NodeResult node={node} />
        </DesktopModal>
    )
}
